import { Info, X } from "lucide-react";
import { useEffect, useState } from "react";
import { ethers, BrowserProvider, Contract } from "ethers";
import contractABI from "../../abis/contractAbi";

const TransferOwnershipModal = ({ isOpen, onClose, address, walletAddress, onRefresh }) => {
  const [newOwner, setNewOwner] = useState("");
  const [isOwner, setIsOwner] = useState(false);
  const [loading, setLoading] = useState(false);
  const [contractInstance, setContractInstance] = useState(null);

  // Initialize contract
  useEffect(() => {
    if (!address || !walletAddress || !contractABI) return;

    async function initContract() {
      try {
        const provider = new BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();
        const contract = new Contract(address, contractABI, signer);
        setContractInstance(contract);
      } catch (err) {
        console.error("Failed to initialize contract", err);
      }
    }

    initContract();
  }, [address, walletAddress]);

  // Check ownership
  useEffect(() => {
    if (!contractInstance || !walletAddress) return;

    async function checkOwner() {
      try {
        const owner = await contractInstance.owner();
        setIsOwner(owner.toLowerCase() === walletAddress.toLowerCase());
      } catch (err) {
        console.error("Error checking owner", err);
      }
    }

    checkOwner();
  }, [contractInstance, walletAddress]);

  const handleTransferOwnership = async () => {
    if (!contractInstance || !isOwner || !ethers.isAddress(newOwner)) return;
    setLoading(true);
    try {
      const tx = await contractInstance.transferOwnership(newOwner);
      await tx.wait();
      onRefresh?.();
      setNewOwner("");
      onClose?.();
    } catch (err) {
      console.error("Ownership transfer failed", err);
    } finally {
      setLoading(false);
    }
  };
 
  if (!isOpen) return null;

  return (
    <div className="fixed h-[100vh] inset-0 z-50 bg-black/50 flex items-center justify-center"
    onClick={(e) => {
    // Close modal if user clicks the backdrop (not the modal content)
    if (e.target === e.currentTarget) onClose();
  }}
    >
      <div className="relative backdrop-blur-md bg-white/80 dark:bg-slate-900/90 border border-emerald-100 dark:border-slate-700 shadow-2xl rounded-3xl p-8 w-full max-w-xl mx-auto">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-600 dark:text-gray-300 hover:text-red-500"
        >
          <X className="w-5 h-5" />
        </button>

        <h2 className="text-2xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-emerald-500 to-cyan-500">
          Transfer Contract Ownership
        </h2>

        <label className="block text-sm font-semibold text-emerald-700 dark:text-emerald-300 mb-2">
          New Owner Address
        </label>
        <input
          type="text"
          value={newOwner}
          onChange={(e) => setNewOwner(e.target.value)}
          placeholder="0x..."
          className="w-full p-3 mb-5 border border-emerald-200 dark:border-slate-600 rounded-xl text-sm shadow-inner focus:outline-none focus:ring-2 focus:ring-emerald-400 dark:bg-slate-800 dark:text-white"
        />
        <div className="flex items-center justify-between">

          <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-2">
            <Info className="w-4 h-4" />
            Only the contract owner can transfer ownership.
          </div>
          <button
            onClick={handleTransferOwnership}
            disabled={!isOwner || loading || !ethers.isAddress(newOwner)}
            className={`px-6 py-3 rounded-xl font-semibold text-white transition-all ${
              isOwner
                ? "bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-600 hover:to-cyan-600 shadow-md"
                : "bg-gradient-to-r from-gray-400 to-gray-600 opacity-80 cursor-not-allowed"
            }`}
          >
            {loading ? "Transferring..." : "Transfer Ownership"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransferOwnershipModal;
